// HTTP Client - Thin HTTP wrapper
//
// This is a simple, dumb HTTP client that:
// 1. Builds full URLs from endpoint paths
// 2. Adds authorization headers from sessionStorage
// 3. Handles timeouts and parses JSON responses
// 4. Returns errors for the caller to handle

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.oxygen.businessfundamentals.nl';
const API_TIMEOUT = 300000; // 5 minutes

/**
 * Create a fetch request with timeout
 */
const fetchWithTimeout = (url, options = {}, timeout = API_TIMEOUT) => {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), timeout)
    ),
  ]);
};

/**
 * Handle response and parse JSON (or return the raw Blob for file downloads,
 * via `responseType: 'blob'` — error bodies are still parsed as JSON/text)
 */
const handleResponse = async (response, responseType) => {
  if (responseType === 'blob' && response.ok) {
    return response.blob();
  }

  const contentType = response.headers.get('content-type');
  // Also matches `+json` types, e.g. ProblemDetails' application/problem+json.
  const isJson = /application\/([\w.-]+\+)?json/.test(contentType ?? '');
  
  const data = isJson ? await response.json() : await response.text();
  
  if (!response.ok) {
    const error = new Error(data.message || 'Request failed');
    error.status = response.status;
    error.response = { data, status: response.status };
    throw error;
  }
  
  return data;
};

/**
 * HTTP Client - Simple HTTP wrapper
 * This is the core client that just makes HTTP requests
 */
const httpClient = async (endpoint, options = {}) => {
  // Build full URL - endpoint can be relative (/auth/access) or full path (/api/v1/auth/access)
  let url;
  if (endpoint.startsWith('http')) {
    url = endpoint;
  } else if (endpoint.startsWith('/api/v1')) {
    url = `${API_BASE_URL}${endpoint}`;
  } else {
    url = `${API_BASE_URL}/api/v1${endpoint}`;
  }
  
  // Default headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add authorization token from sessionStorage if available
  const token = sessionStorage.getItem('accessToken');
  if (token && !options.skipAuth) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Merge options (responseType is ours, not a fetch option)
  const { responseType, ...fetchOptions } = options;
  const config = {
    ...fetchOptions,
    headers,
  };

  const response = await fetchWithTimeout(url, config);
  return { data: await handleResponse(response, responseType) };
};

export default httpClient;
